import React, { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ImportSwaggerForm from '../components/ImportSwagger/ImportSwaggerForm';
import ImportSwaggerPreview from '../components/ImportSwagger/ImportSwaggerPreview';
import ImportSwaggerProgress from '../components/ImportSwagger/ImportSwaggerProgress';
import ImportSwaggerDataSets from '../components/ImportSwagger/ImportSwaggerDataSets';
import ImportSwaggerAssertions from '../components/ImportSwagger/ImportSwaggerAssertions';
import ImportSwaggerResult from '../components/ImportSwagger/ImportSwaggerResult';
import { filterSwaggerSelectionsForImport, buildSwaggerEnvironmentImportOptions } from '../components/ImportSwagger/importSwaggerUtils';
import { useProjects } from '../context/ProjectContext';
import { apiService, extractApiErrorMessage } from '../services/apiService';
import { dispatchEnvironmentsChanged } from '../utils/environmentVariablesEvents';
import { getHelpPathForPage } from '../utils/helpPageMapping';
import { DFT_BRAND, zoomedMainMinHeight } from '../styles/brandPalette';
import { PROJECT_SELECT_CONTROL_WIDTH } from './Projects';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DatasetIcon from '@mui/icons-material/Dataset';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const SWAGGER_CARD_RADIUS = '14px';

/** Viewport-anchored Select menu — avoids CSS-zoom misalignment on tenant/server layouts. */
function useZoomSafeSelectMenu(extra?: {
  paperSx?: Record<string, unknown>;
  menuListSx?: Record<string, unknown>;
}) {
  const [menuState, setMenuState] = useState({
    open: false,
    top: 0,
    left: 0,
    width: 0,
    openUpward: false,
  });
  const anchorElRef = useRef<HTMLElement | null>(null);

  const measureFromEl = (combobox: HTMLElement) => {
    const rect = combobox.getBoundingClientRect();
    const gap = 4;
    const spaceBelow = typeof window !== 'undefined' ? window.innerHeight - rect.bottom : 320;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < 120 && spaceAbove > spaceBelow;
    return {
      top: Math.round(openUpward ? rect.top - gap : rect.bottom + gap),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      openUpward,
    };
  };

  const onOpen = (e: React.SyntheticEvent) => {
    const el = e.currentTarget as HTMLElement;
    const combobox =
      (el.matches('[role="combobox"]')
        ? el
        : el.querySelector<HTMLElement>('[role="combobox"]')) ?? el;
    anchorElRef.current = combobox;
    const measured = measureFromEl(combobox);
    flushSync(() => {
      setMenuState({ open: true, ...measured });
    });
  };

  const onClose = () => setMenuState((prev) => ({ ...prev, open: false }));

  const remeasureWhileOpen = () => {
    const combobox = anchorElRef.current;
    if (!combobox) return;
    const measured = measureFromEl(combobox);
    setMenuState((prev) => (prev.open ? { ...prev, ...measured } : prev));
  };

  const MenuProps = {
    disableScrollLock: true,
    anchorReference: 'anchorPosition' as const,
    anchorPosition: { top: menuState.top, left: menuState.left },
    transformOrigin: {
      vertical: (menuState.openUpward ? 'bottom' : 'top') as 'bottom' | 'top',
      horizontal: 'left' as const,
    },
    sx: { zIndex: (theme: import('@mui/material/styles').Theme) => theme.zIndex.modal },
    TransitionProps: {
      onEntering: remeasureWhileOpen,
      onEntered: remeasureWhileOpen,
    },
    PaperProps: {
      sx: {
        ...(menuState.openUpward ? { mb: 0.5, mt: 0 } : { mt: 0.5 }),
        zIndex: (theme: import('@mui/material/styles').Theme) => theme.zIndex.modal,
        ...(menuState.width > 0
          ? {
              width: menuState.width,
              minWidth: menuState.width,
              maxWidth: 'min(100vw - 16px, 560px)',
            }
          : {}),
        ...extra?.paperSx,
      },
    },
    MenuListProps: {
      sx: {
        maxHeight: 320,
        overflowY: 'auto' as const,
        py: 0.5,
        ...extra?.menuListSx,
      },
    },
  };

  return { menuOpen: menuState.open, onOpen, onClose, MenuProps };
}

const swaggerPageSx = {
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  alignSelf: 'stretch',
  flex: 1,
  px: 0,
  py: 0,
  bgcolor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'hidden',
  boxSizing: 'border-box' as const,
  minHeight: {
    xs: 'calc(100dvh - 64px)',
    ...zoomedMainMinHeight(),
  },
  '& .MuiCard-root': {
    borderRadius: `${SWAGGER_CARD_RADIUS} !important`,
    overflow: 'hidden',
  },
  '& .MuiAlert-root': {
    borderRadius: '12px',
  },
  '& .MuiTypography-subtitle2': {
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  '& .MuiTypography-body2': {
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
  },
} as const;
const SWAGGER_WORKSPACE_SX = {
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '0 12px 12px 12px',
  border: `1px solid ${DFT_BRAND.line}`,
  borderTop: `1px solid ${DFT_BRAND.line}`,
  bgcolor: DFT_BRAND.bg,
  boxShadow: 'none',
  overflow: 'hidden',
} as const;
const swaggerCanvasSx = {
  flex: '1 1 auto',
  minHeight: 0,
  overflow: 'auto',
  bgcolor: DFT_BRAND.bg,
  p: { xs: 2, sm: 2.5 },
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 2, sm: 2.5 },
} as const;
const swaggerCardSx = {
  position: 'relative' as const,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  border: `1px solid ${DFT_BRAND.line}`,
  borderRadius: `${SWAGGER_CARD_RADIUS} !important`,
  boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
  bgcolor: DFT_BRAND.paper,
  '&::before': {
    content: '""',
    position: 'absolute',
    height: 3,
    left: 0,
    right: 0,
    top: 0,
    background: DFT_BRAND.metricAccent,
    borderRadius: `${SWAGGER_CARD_RADIUS} ${SWAGGER_CARD_RADIUS} 0 0`,
  },
} as const;

const steps = [
  { label: 'Enter Details', icon: <AssignmentIcon sx={{ fontSize: { xs: 20, sm: 22, lg: 24 } }} /> },
  { label: 'Preview', icon: <VisibilityIcon sx={{ fontSize: { xs: 20, sm: 22, lg: 24 } }} /> },
  { label: 'Import', icon: <CloudDownloadIcon sx={{ fontSize: { xs: 20, sm: 22, lg: 24 } }} /> },
  { label: 'Data Sets', icon: <DatasetIcon sx={{ fontSize: { xs: 20, sm: 22, lg: 24 } }} /> },
  { label: 'Assertions', icon: <VerifiedUserIcon sx={{ fontSize: { xs: 20, sm: 22, lg: 24 } }} /> },
  { label: 'Result', icon: <CheckCircleIcon sx={{ fontSize: { xs: 20, sm: 22, lg: 24 } }} /> },
];

/** Survives leaving /swagger so the import report is still there on return. */
const SWAGGER_IMPORT_VIEW_KEY = 'crud.swagger.importView';

type SwaggerImportView = {
  projectId: number | null;
  activeStep: number;
  importProgress: number;
  importResult: any;
  dataSetsCreated: number;
  dataSetsError: string | null;
  assertionsCreated: number;
  assertionsError: string | null;
};

let swaggerImportGeneration = 0;
let swaggerImportInFlight = false;
const swaggerImportListeners = new Set<(view: SwaggerImportView) => void>();

function readSwaggerImportView(): SwaggerImportView | null {
  try {
    const raw = sessionStorage.getItem(SWAGGER_IMPORT_VIEW_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as SwaggerImportView;
    if (!data || typeof data.activeStep !== 'number') return null;
    if (data.activeStep === 2) return data;
    if (data.activeStep >= 3 && data.activeStep <= 5 && data.importResult) return data;
    return null;
  } catch {
    return null;
  }
}

function viewForHydrate(): SwaggerImportView | null {
  const saved = readSwaggerImportView();
  if (!saved) return null;
  if (saved.activeStep === 2 && !swaggerImportInFlight) return null;
  return saved;
}

function writeSwaggerImportView(view: SwaggerImportView | null) {
  try {
    if (!view) {
      sessionStorage.removeItem(SWAGGER_IMPORT_VIEW_KEY);
      return;
    }
    const existing = readSwaggerImportView();
    if (existing && existing.activeStep > view.activeStep) return;
    sessionStorage.setItem(SWAGGER_IMPORT_VIEW_KEY, JSON.stringify(view));
  } catch {
    /* ignore quota / private mode */
  }
}

function publishSwaggerImportView(view: SwaggerImportView) {
  writeSwaggerImportView(view);
  swaggerImportListeners.forEach((listener) => listener(view));
}

const Swagger: React.FC = () => {
  const { projects, selectedProject, setSelectedProject, isLoading: projectsLoading } = useProjects();
  const [collections, setCollections] = useState<any[]>([]);
  const [activeStep, setActiveStep] = useState(() => viewForHydrate()?.activeStep ?? 0);
  const [projectId, setProjectIdState] = useState<number | null>(() => viewForHydrate()?.projectId ?? null);
  const [resumeAssertions, setResumeAssertions] = useState(() => viewForHydrate()?.activeStep === 4);

  const setProjectId = useCallback(
    (id: number) => {
      setProjectIdState(id);
      const p = projects.find((pr) => Number(pr.id) === Number(id));
      setSelectedProject(p ?? null);
    },
    [projects, setSelectedProject]
  );

  // Align with app-wide project selection (same as Environments / Test Cycles).
  useEffect(() => {
    if (projectId === null && selectedProject?.id != null) {
      setProjectIdState(Number(selectedProject.id));
    }
  }, [selectedProject, projectId]);
  const [swaggerUrl, setSwaggerUrl] = useState('');
  const [swaggerFile, setSwaggerFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<any>(null);
  const [createEnvironment, setCreateEnvironment] = useState(true);
  const [importProgress, setImportProgress] = useState<number>(() => viewForHydrate()?.importProgress ?? 0);
  const [importResult, setImportResult] = useState<any>(() => viewForHydrate()?.importResult ?? null);
  const [dataSetsCreated, setDataSetsCreated] = useState<number>(() => viewForHydrate()?.dataSetsCreated ?? 0);
  const [dataSetsError, setDataSetsError] = useState<string | null>(() => viewForHydrate()?.dataSetsError ?? null);
  const [assertionsCreated, setAssertionsCreated] = useState<number>(() => viewForHydrate()?.assertionsCreated ?? 0);
  const [assertionsError, setAssertionsError] = useState<string | null>(() => viewForHydrate()?.assertionsError ?? null);
  const navigate = useNavigate();
  const swaggerProjectSelectRef = useRef<HTMLDivElement>(null);
  const [swaggerProjectMenuWidth, setSwaggerProjectMenuWidth] = useState(0);
  const projectZoomMenu = useZoomSafeSelectMenu({
    paperSx:
      swaggerProjectMenuWidth > 0
        ? {
            width: `${swaggerProjectMenuWidth}px !important`,
            minWidth: `${swaggerProjectMenuWidth}px !important`,
            maxWidth: `${swaggerProjectMenuWidth}px !important`,
          }
        : undefined,
    menuListSx: {
      '& .MuiMenuItem-root': {
        minHeight: 40,
        whiteSpace: 'nowrap',
        overflow: 'visible',
      },
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    },
  });

  // Always fetch collections for the selected projectId
  useEffect(() => {
    const fetchCollections = async () => {
      if (projectId) {
        setLoading(true);
        try {
          const data = await apiService.getCollectionsByProject(projectId);
          setCollections(data);
        } catch (err) {
          setCollections([]);
        } finally {
          setLoading(false);
        }
      } else {
        setCollections([]);
      }
    };
    fetchCollections();
  }, [projectId]);

  const applySavedImport = useCallback((view: SwaggerImportView) => {
    setProjectIdState(view.projectId);
    setActiveStep(view.activeStep);
    setImportProgress(view.activeStep === 4 ? Math.min(view.importProgress || 0, 99) : view.importProgress);
    setImportResult(view.importResult);
    setDataSetsCreated(view.dataSetsCreated || 0);
    setDataSetsError(view.dataSetsError ?? null);
    setAssertionsCreated(view.assertionsCreated || 0);
    setAssertionsError(view.assertionsError ?? null);
    if (view.activeStep !== 4) setResumeAssertions(false);
  }, []);

  useEffect(() => {
    if (activeStep < 2 || (activeStep >= 3 && !importResult)) {
      if (activeStep < 2) writeSwaggerImportView(null);
      return;
    }
    writeSwaggerImportView({
      projectId,
      activeStep,
      importProgress,
      importResult,
      dataSetsCreated,
      dataSetsError,
      assertionsCreated,
      assertionsError,
    });
  }, [
    activeStep,
    projectId,
    importProgress,
    importResult,
    dataSetsCreated,
    dataSetsError,
    assertionsCreated,
    assertionsError,
  ]);

  useEffect(() => {
    swaggerImportListeners.add(applySavedImport);
    const latest = readSwaggerImportView();
    if (latest) applySavedImport(latest);
    return () => {
      swaggerImportListeners.delete(applySavedImport);
    };
  }, [applySavedImport]);

  useEffect(() => {
    if (!resumeAssertions || activeStep !== 4 || projectId == null) return;
    let cancelled = false;
    const pull = async () => {
      try {
        const live = await apiService.getSwaggerImportProgress(projectId);
        if (cancelled) return;
        const percent = typeof live.progressPercent === 'number' ? live.progressPercent : 0;
        setImportProgress(percent);
        const label = live.progressLabel || '';
        if (percent >= 100 || /assertions created/i.test(label)) {
          if (typeof live.assertionsCreated === 'number') {
            setAssertionsCreated(live.assertionsCreated);
          }
          setResumeAssertions(false);
          setActiveStep(5);
        }
      } catch {
        /* assertions request is still running on the server */
      }
    };
    void pull();
    const timer = window.setInterval(() => {
      void pull();
    }, 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [resumeAssertions, activeStep, projectId]);

  const handleFormSubmit = async () => {
    setLoading(true);
    setError(null);
    setPreviewError(null);
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      // Use the new preview API that includes environment detection
      const previewOptions = swaggerFile
        ? {
            openApiSpec: await swaggerFile.text(),
            specFileName: swaggerFile.name,
          }
        : { url: swaggerUrl };
      const data = await apiService.previewSwagger(projectId, previewOptions);
      if (data && (data as { success?: boolean }).success === false) {
        const fail = data as { message?: string; error?: string; collections?: unknown[] };
        const hasPreviewCollections =
          Array.isArray(fail.collections) && fail.collections.length > 0;
        if (!hasPreviewCollections) {
          throw {
            response: {
              data: {
                message: fail.message || fail.error || 'Failed to parse OpenAPI spec.',
              },
            },
          };
        }
      }
      setParsedData(data);
      setActiveStep(1);
    } catch (e: any) {
      const data = e?.response?.data;
      const message =
        (typeof data?.message === 'string' && data.message.trim()) ||
        (typeof data?.error === 'string' && data.error.trim()) ||
        (e?.message && !String(e.message).includes('status code') ? e.message : null) ||
        'Failed to parse OpenAPI spec. Check the URL is reachable and returns valid OpenAPI JSON or YAML.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewSubmit = async (selections: any) => {
    const filteredCollections = filterSwaggerSelectionsForImport(selections);
    if (filteredCollections.length === 0) {
      setPreviewError('Select at least one collection with at least one request to import.');
      return;
    }
    setPreviewError(null);
    setActiveStep(2);
    setImportProgress(0);
    const runId = ++swaggerImportGeneration;
    swaggerImportInFlight = true;

    try {
      setImportProgress(30);
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      
      // Associate imported requests with the preview environment (reuse existing when base URL matches).
      const envFromPreview = parsedData?.environments?.[0];
      const { createEnvironment: createEnvironmentForImport, environmentInfo: environmentInfoForImport } =
        buildSwaggerEnvironmentImportOptions({
          createEnvironment: createEnvironment === true,
          previewEnvironment: envFromPreview,
          swaggerUrl,
          isFileImport: !!swaggerFile,
        });
      setImportProgress(60);
      const result = await apiService.importSwagger({
        ...(swaggerFile
          ? {
              openApiSpec: await swaggerFile.text(),
              specFileName: swaggerFile.name,
            }
          : { url: swaggerUrl }),
        projectId: projectId as number,
        createEnvironment: createEnvironmentForImport,
        environmentInfo: environmentInfoForImport,
        collections: filteredCollections
      });
      
      setImportProgress(100);
      setImportResult(result);
      if (runId !== swaggerImportGeneration) return;

      const finishedView = {
        projectId,
        importProgress: 100,
        importResult: result,
        dataSetsCreated: 0,
        dataSetsError: null,
        assertionsCreated: 0,
        assertionsError: null,
      };

      if (!result?.success) {
        publishSwaggerImportView({ ...finishedView, activeStep: 5 });
        setActiveStep(5);
        return;
      }

      const collectionsCreated = result.collectionsCreated ?? 0;
      const requestsCreated = result.requestsCreated ?? 0;
      if (collectionsCreated === 0 && requestsCreated === 0) {
        publishSwaggerImportView({ ...finishedView, activeStep: 5 });
        setActiveStep(5);
        return;
      }

      publishSwaggerImportView({ ...finishedView, activeStep: 3 });
      setActiveStep(3);
      if (projectId) {
        try {
          const updatedCollections = await apiService.getCollectionsByProject(projectId);
          setCollections(updatedCollections);
        } catch (refreshErr) {
          console.warn('Swagger import succeeded but collection refresh failed', refreshErr);
        }
        dispatchEnvironmentsChanged({
          projectId,
          environmentId:
            typeof result.importEnvironmentId === 'number' ? result.importEnvironmentId : undefined,
          source: 'swagger-import',
        });
      }
    } catch (e: any) {
      if (runId !== swaggerImportGeneration) return;
      setImportProgress(100);
      const data = e?.response?.data;
      if (data && typeof data === 'object' && (data.message || data.success === false || data.error)) {
        const backendMessage =
          (typeof data.message === 'string' && data.message.trim()) ||
          (typeof data.error === 'string' && data.error.trim()) ||
          'Import failed. Check the Swagger URL, project selection, and plan limits, then try again.';
        const errors =
          Array.isArray(data.errors) && data.errors.length > 0
            ? data.errors.map((err: unknown) => String(err))
            : [backendMessage];
        const failedResult = {
          success: false,
          message: backendMessage,
          errors,
          collectionsCreated: data.collectionsCreated ?? 0,
          requestsCreated: data.requestsCreated ?? 0,
        };
        setImportResult(failedResult);
        publishSwaggerImportView({
          projectId,
          activeStep: 5,
          importProgress: 100,
          importResult: failedResult,
          dataSetsCreated: 0,
          dataSetsError: null,
          assertionsCreated: 0,
          assertionsError: null,
        });
      } else {
        const message =
          extractApiErrorMessage(e) ||
          'Import failed. Check the Swagger URL, project selection, and plan limits, then try again.';
        const failedResult = {
          success: false,
          message,
          errors: [message],
          collectionsCreated: 0,
          requestsCreated: 0,
        };
        setImportResult(failedResult);
        publishSwaggerImportView({
          projectId,
          activeStep: 5,
          importProgress: 100,
          importResult: failedResult,
          dataSetsCreated: 0,
          dataSetsError: null,
          assertionsCreated: 0,
          assertionsError: null,
        });
      }
      // Skip Data Sets, Assertions steps on error, go directly to Result (step 5)
      setActiveStep(5);
    } finally {
      if (runId === swaggerImportGeneration) swaggerImportInFlight = false;
    }
  };

  const handleDataSetsComplete = useCallback((count: number) => {
    setDataSetsCreated(count);
    // Move to Assertions step
    setActiveStep(4);
  }, []);

  const handleDataSetsError = useCallback((error: string) => {
    setDataSetsError(error);
    // Still move to Assertions step (they can still be created)
    setActiveStep(4);
  }, []);

  const handleAssertionsComplete = useCallback((count: number) => {
    setAssertionsCreated(count);
    setActiveStep(5);
  }, []);

  const handleAssertionsError = useCallback((error: string) => {
    setAssertionsError(error);
    setActiveStep(5);
  }, []);

  const handleReset = () => {
    swaggerImportGeneration += 1;
    swaggerImportInFlight = false;
    writeSwaggerImportView(null);
    setResumeAssertions(false);
    setActiveStep(0);
    setProjectIdState(selectedProject?.id ?? null);
    setSwaggerUrl('');
    setSwaggerFile(null);
    setParsedData(null);
    setCreateEnvironment(true);
    setImportProgress(0);
    setImportResult(null);
    setDataSetsCreated(0);
    setDataSetsError(null);
    setAssertionsCreated(0);
    setAssertionsError(null);
    setError(null);
    setPreviewError(null);
  };

  return (
    <Box sx={swaggerPageSx}>
      <Box sx={{ width: '100%', flexShrink: 0, overflow: 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1,
            minWidth: 0,
            px: { xs: 1.25, sm: 1.5 },
            pt: 1.5,
            pb: 2,
            minHeight: 36,
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              m: 0,
              fontWeight: 700,
              height: 36,
              lineHeight: '36px',
              display: 'flex',
              alignItems: 'center',
              letterSpacing: '-0.02em',
              color: DFT_BRAND.text,
            }}
          >
            Swagger
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minHeight: 36,
              '& .MuiButton-root': {
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1,
              },
              '& .MuiButton-startIcon': {
                display: 'inline-flex',
                alignItems: 'center',
                marginTop: 0,
                marginBottom: 0,
              },
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<HelpOutlineIcon />}
              onClick={() => navigate(getHelpPathForPage('SWAGGER'))}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#1d4ed8',
                borderColor: '#bfdbfe',
                bgcolor: '#ffffff',
                height: 36,
                minHeight: 36,
                borderRadius: 0,
                px: 1.1,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: '#93c5fd',
                  bgcolor: '#eff6ff',
                },
              }}
              aria-label="Open help for Swagger"
            >
              Help
            </Button>
            <FormControl
              ref={swaggerProjectSelectRef}
              size="small"
              variant="outlined"
              sx={{
                width: { xs: 180, sm: 220, md: 240, lg: PROJECT_SELECT_CONTROL_WIDTH.sm },
                flexShrink: 0,
                alignSelf: 'center',
                overflow: 'visible',
                '& .MuiInputLabel-root': {
                  backgroundColor: DFT_BRAND.paper,
                  px: 0.5,
                  lineHeight: 1.25,
                  zIndex: 1,
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '& .MuiOutlinedInput-root': {
                  height: 36,
                  minHeight: 36,
                  backgroundColor: DFT_BRAND.paper,
                  borderRadius: '10px',
                },
              }}
              aria-label="Select project"
            >
              <InputLabel id="swagger-project-select-label" shrink>
                Project
              </InputLabel>
              <Select
                labelId="swagger-project-select-label"
                id="swagger-project-select"
                value={projectId != null ? String(projectId) : ''}
                label="Project"
                open={projectZoomMenu.menuOpen}
                onOpen={(e) => {
                  const width = swaggerProjectSelectRef.current?.getBoundingClientRect().width ?? 0;
                  if (width > 0) setSwaggerProjectMenuWidth(Math.round(width));
                  projectZoomMenu.onOpen(e);
                }}
                onClose={projectZoomMenu.onClose}
                MenuProps={projectZoomMenu.MenuProps}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  if (Number.isFinite(next)) setProjectId(next);
                }}
                sx={{
                  width: '100%',
                  bgcolor: DFT_BRAND.paper,
                  '& .MuiSelect-select': {
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    lineHeight: 1.35,
                    display: 'block',
                    boxSizing: 'border-box',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                {projectsLoading ? (
                  <MenuItem disabled value="">
                    <CircularProgress size={20} /> Loading...
                  </MenuItem>
                ) : (
                  [
                    <MenuItem key="empty" value="" disabled>
                      Select a project
                    </MenuItem>,
                    ...projects.map((project) => (
                      <MenuItem key={project.id} value={String(project.id)} sx={{ fontSize: '0.875rem', py: 1 }}>
                        {project.name}
                      </MenuItem>
                    )),
                  ]
                )}
                {projects.length === 0 && !projectsLoading && (
                  <MenuItem disabled>No projects found</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box sx={SWAGGER_WORKSPACE_SX}>
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 1.25, sm: 2 },
            pt: { xs: 1.75, sm: 2 },
            pb: { xs: 1.5, sm: 1.75 },
            borderBottom: `1px solid ${DFT_BRAND.line}`,
            bgcolor: DFT_BRAND.paper,
            overflowX: 'auto',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              width: '100%',
              minWidth: { xs: 560, sm: 0 },
            }}
          >
            {steps.map((step, idx) => {
              const stepComplete =
                idx < activeStep &&
                !(activeStep === 5 && importResult?.success === false && idx > 2);
              const isActive = idx === activeStep;
              const connectorDone =
                idx <= activeStep &&
                !(activeStep === 5 && importResult?.success === false && idx > 2);
              return (
                <React.Fragment key={step.label}>
                  {idx > 0 && (
                    <Box
                      aria-hidden
                      sx={{
                        flex: '1 1 16px',
                        minWidth: { xs: 10, sm: 14, md: 20 },
                        height: 3,
                        mt: { xs: '16px', sm: '18px', lg: '20px' },
                        borderRadius: 99,
                        background: connectorDone
                          ? `linear-gradient(100deg, ${DFT_BRAND.primaryStart}, ${DFT_BRAND.primaryEnd})`
                          : DFT_BRAND.line,
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      flex: '0 0 auto',
                      minWidth: { xs: 64, sm: 80, md: 96, lg: 108 },
                      maxWidth: { xs: 80, sm: 108, md: 124 },
                      px: { xs: 0.5, sm: 0.75 },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 36, sm: 40, lg: 44 },
                        height: { xs: 36, sm: 40, lg: 44 },
                        borderRadius: '50%',
                        background: stepComplete
                          ? DFT_BRAND.chartGreen
                          : isActive
                            ? `linear-gradient(100deg, ${DFT_BRAND.primaryStart}, ${DFT_BRAND.primaryEnd})`
                            : DFT_BRAND.tint,
                        color: stepComplete || isActive ? '#fff' : DFT_BRAND.icon,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1,
                        boxShadow: isActive ? DFT_BRAND.primaryShadow : 'none',
                        border: `1px solid ${
                          isActive
                            ? DFT_BRAND.primaryStart
                            : stepComplete
                              ? DFT_BRAND.chartGreen
                              : DFT_BRAND.line
                        }`,
                        transition: 'all 0.2s',
                      }}
                    >
                      {stepComplete ? (
                        <CheckCircleIcon sx={{ fontSize: { xs: 20, sm: 22, lg: 24 } }} />
                      ) : (
                        step.icon
                      )}
                    </Box>
                    <Typography
                      component="span"
                      title={step.label}
                      sx={{
                        fontWeight: isActive ? 700 : 600,
                        color: isActive ? DFT_BRAND.text : DFT_BRAND.muted,
                        textAlign: 'center',
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                        lineHeight: 1.25,
                        letterSpacing: '-0.01em',
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>

        <Box sx={swaggerCanvasSx}>
        <Box sx={{ ...swaggerCardSx, px: { xs: 1.75, sm: 2.25 }, pt: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 } }}>
          {activeStep === 0 && (
            <ImportSwaggerForm
              projects={projects}
              projectsLoading={projectsLoading}
              projectId={projectId}
              setProjectId={setProjectId}
              swaggerUrl={swaggerUrl}
              setSwaggerUrl={setSwaggerUrl}
              swaggerFile={swaggerFile}
              setSwaggerFile={setSwaggerFile}
              createEnvironment={createEnvironment}
              setCreateEnvironment={setCreateEnvironment}
              onSubmit={handleFormSubmit}
              error={error}
              loading={loading}
              hideProjectSelect
            />
          )}
          {activeStep === 1 && (
            <ImportSwaggerPreview
              projectId={projectId}
              parsedData={parsedData}
              existingCollections={collections}
              environmentInfo={parsedData?.environmentInfo}
              onSubmit={handlePreviewSubmit}
              onBack={() => { setPreviewError(null); setActiveStep(0); }}
              submitError={previewError}
            />
          )}
          {activeStep === 2 && (
            <ImportSwaggerProgress progress={importProgress} />
          )}
          {activeStep === 3 && projectId && importResult && importResult.success && (
            <ImportSwaggerDataSets
              projectId={projectId}
              collectionsCreated={importResult.collectionsCreated || 0}
              requestsCreated={importResult.requestsCreated || 0}
              onComplete={handleDataSetsComplete}
              onError={handleDataSetsError}
            />
          )}
          {activeStep === 4 && resumeAssertions && (
            <ImportSwaggerProgress progress={importProgress} />
          )}
          {activeStep === 4 && !resumeAssertions && projectId && importResult && importResult.success && (
            <ImportSwaggerAssertions
              projectId={projectId}
              dataSetsCreated={dataSetsCreated}
              onComplete={handleAssertionsComplete}
              onError={handleAssertionsError}
            />
          )}
          {activeStep === 5 && (
            <ImportSwaggerResult 
              result={{
                ...importResult,
                dataSetsCreated:
                  (Number(importResult?.dataSetsCreated) || 0) + (Number(dataSetsCreated) || 0),
                dataSetsError,
                assertionsCreated,
                assertionsError,
              }} 
              onClose={handleReset} 
              onReset={handleReset} 
            />
          )}
        </Box>
        {activeStep === 0 && (
        <Box sx={{ ...swaggerCardSx, px: { xs: 1.75, sm: 2.25 }, pt: 2, pb: 1.75 }}>
          <Typography
            sx={{
              fontWeight: 700,
              mb: 1.25,
              color: DFT_BRAND.text,
              letterSpacing: '-0.01em',
              fontSize: '0.875rem',
            }}
          >
            How to use
          </Typography>
          <Box
            component="ol"
            sx={{
              m: 0,
              pl: 2.25,
              color: DFT_BRAND.muted,
              fontSize: '0.875rem',
              lineHeight: 1.7,
            }}
          >
            <li>Select the project from project dropdown</li>
            <li>Enter your Swagger/OpenAPI URL or choose a spec file</li>
            <li>Follow the steps to import endpoints</li>
          </Box>
        </Box>
        )}
        </Box>
      </Box>
    </Box>
  );
};

export default Swagger; 